import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@powerful/ui';
import { signInSchema } from '@powerful/types';
import type { z } from 'zod';

import { AuthScreen, AuthScreenSpacer } from '../components/auth/auth-screen';
import { AuthTextField, MailIcon } from '../components/auth/auth-text-field';
import { PasswordField } from '../components/auth/password-field';
import { SocialButtons } from '../components/auth/social-buttons';
import { accountDeletionNoticeStore } from '../lib/account/account-deletion-notice';
import { signIn } from '../lib/auth/auth-client';
import { authErrorKey } from '../lib/auth/auth-error-key';
import { safeReturnTo } from '../lib/auth/auth-navigation';
import { pendingSignupStore } from '../lib/auth/pending-signup';
import { zodIssueMessage } from '../i18n/zod-i18n';

type FieldErrors = Partial<Record<'email' | 'password', z.ZodIssue>>;

/**
 * Entrar (canvas `Login`).
 *
 * 🔴 Credenciais erradas usam **uma** mensagem para os dois campos. Distinguir "e-mail não existe"
 * de "palavra-passe errada" transforma o formulário de login num verificador de contas: quem tem a
 * lista de e-mails descobre quais estão registados sem nunca acertar uma palavra-passe.
 *
 * O destino pós-login vem de `state.from` e passa pelo `safeReturnTo`, que só aceita caminhos
 * relativos declarados no manifesto. Um `from` absoluto, `//host` ou rota desconhecida converge
 * para `/` — é a defesa contra open redirect (§3.1 do plano).
 */
export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverErrorKey, setServerErrorKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLDivElement>(null);

  const destino = safeReturnTo((location.state as { from?: unknown } | null)?.from);

  // T1.17: quem acabou de pedir a exclusão aterra aqui sem sessão. Lido no inicializador e apagado
  // em efeito (seguro sob StrictMode); aparece uma vez e só enquanto este ecrã estiver montado.
  const [deletionNotice] = useState(() => accountDeletionNoticeStore.read());
  useEffect(() => {
    if (deletionNotice) accountDeletionNoticeStore.clear();
  }, [deletionNotice]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    // Duplo-submit bloqueado pelo estado, não só pelo `disabled`: entre o clique e o re-render há
    // uma janela em que o botão ainda aceita o segundo clique.
    if (submitting) return;

    const validation = signInSchema.safeParse({ email, password });
    if (!validation.success) {
      const errors: FieldErrors = {};
      for (const issue of validation.error.issues) {
        const campo = issue.path[0];
        if (campo === 'email' || campo === 'password') errors[campo] ??= issue;
      }
      setFieldErrors(errors);
      setServerErrorKey(null);
      // Foco no primeiro erro — quem navega por teclado não tem como descobrir sozinho onde está.
      if (errors.email) emailRef.current?.focus();
      else passwordRef.current?.querySelector('input')?.focus();
      return;
    }

    setFieldErrors({});
    setServerErrorKey(null);
    setSubmitting(true);

    const result = await signIn(validation.data.email, validation.data.password);
    setSubmitting(false);

    if (result.ok) {
      pendingSignupStore.clear();
      navigate(destino, { replace: true });
      return;
    }

    if (result.code === 'email_not_confirmed') {
      // O login só conhece o e-mail. Prazo e papel pertencem ao recibo autoritativo devolvido pelo
      // signup-guard; reconstruí-los aqui produziria informação falsa para contas antigas ou pro.
      pendingSignupStore.write({ email: validation.data.email });
      navigate('/verify');
      return;
    }

    setServerErrorKey(authErrorKey(result.code));
  };

  const clearFieldError = (campo: 'email' | 'password') =>
    setFieldErrors((current) => {
      if (!current[campo]) return current;
      const next = { ...current };
      delete next[campo];
      return next;
    });

  const mensagem = (issue?: z.ZodIssue) =>
    issue ? zodIssueMessage(issue, t('errors:validation.invalidType')) : null;

  return (
    <AuthScreen
      screenLabel="auth-login"
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
      onBack={() => navigate(-1)}
    >
      <div data-testid="login-page" className="flex min-w-0 flex-1 flex-col">
        {deletionNotice ? (
          <div role="status" className="alert alert-info mt-6 min-w-0 text-[length:var(--t-sm)]">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="ic text-[color:var(--info)]" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5" />
              <path d="M12 7.8h.01" />
            </svg>
            <div className="min-w-0">
              <p className="font-semibold text-ink-1">{t('accountDeletion.notice.title')}</p>
              <p className="mt-1 leading-relaxed text-ink-2">
                {t('accountDeletion.notice.body', {
                  date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(
                    new Date(deletionNotice.deletionDueAt),
                  ),
                })}
              </p>
            </div>
          </div>
        ) : null}

        {serverErrorKey ? (
          <div role="alert" className="alert alert-danger mt-6 text-[length:var(--t-sm)] text-danger">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="ic" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7.5v5" />
              <path d="M12 16.2h.01" />
            </svg>
            <span>{t(`errors:${serverErrorKey}`)}</span>
          </div>
        ) : null}

        <form noValidate onSubmit={(event) => void submit(event)} className="flex min-w-0 flex-col">
          {/* Os campos ficam a 55 % e `readOnly` durante a submissão — nunca desmontados, para o
              valor digitado sobreviver a uma falha de rede (canvas `EstadoCarregando`). */}
          <fieldset
            aria-busy={submitting}
            className={`mt-7 flex min-w-0 flex-col gap-5 border-0 p-0 ${submitting ? 'opacity-55' : ''}`}
          >
            <AuthTextField
              ref={emailRef}
              label={t('auth.login.email.label')}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              readOnly={submitting}
              onChange={(event) => {
                setEmail(event.target.value);
                clearFieldError('email');
              }}
              icon={<MailIcon />}
              error={mensagem(fieldErrors.email)}
            />

            <div ref={passwordRef} className="min-w-0">
              <PasswordField
                label={t('auth.login.password.label')}
                autoComplete="current-password"
                value={password}
                readOnly={submitting}
                onChange={(value) => {
                  setPassword(value);
                  clearFieldError('password');
                }}
                error={mensagem(fieldErrors.password)}
              />
            </div>

            <div className="flex justify-end">
              <Link
                to="/password-recovery"
                className="flex min-h-[44px] items-center text-[length:var(--t-sm)] text-gold-text underline underline-offset-4 hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2"
              >
                {t('auth.login.forgot')}
              </Link>
            </div>
          </fieldset>

          <div className="mt-8">
            <Button variant="metal" size="lg" full type="submit" disabled={submitting} aria-busy={submitting}>
              {submitting ? (
                <>
                  <span
                    aria-hidden="true"
                    className="h-[18px] w-[18px] shrink-0 animate-spin rounded-full border-2 border-[color-mix(in_srgb,var(--on-metal)_25%,transparent)] border-t-[color:var(--on-metal)]"
                  />
                  <span>{t('auth.login.submitting')}</span>
                </>
              ) : (
                t('auth.login.cta')
              )}
            </Button>
          </div>
        </form>

        <div className="mt-7">
          <SocialButtons />
        </div>

        <AuthScreenSpacer />

        <div className="mt-6 flex justify-center">
          <span className="flex min-h-[44px] items-center gap-1 text-[length:var(--t-sm)]" lang={i18n.language}>
            <span className="text-ink-3">{t('auth.login.noAccount')}</span>
            <Link className="inline-flex min-h-[44px] items-center text-gold-text underline underline-offset-4 hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2" to="/signup">
              {t('auth.login.signup')}
            </Link>
          </span>
        </div>
      </div>
    </AuthScreen>
  );
}
